/**
 * EvaluatorResponseFrame
 *
 * Shows the evaluator their original intent alongside the response they received.
 * This is the context for making an Accept / Return for revision / Reject decision.
 *
 * Left column: "What you asked for" — original intent, constraints, objectives.
 * Right column: "What came back" — proposals, synthesis, ranked items.
 * Below both: three distinct CTAs (Accept, Return for revision, Reject). No dropdown.
 *
 * Canon: no disabled buttons. Only absent or active CTAs.
 * Return for revision opens an inline guidance field. Reject opens an inline
 * reason field with a warning that it restarts the work.
 */

import { useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  MessageSquare,
  RotateCcw,
  Shield,
  Target,
  XCircle,
} from 'lucide-react';
import type { LoopIntent } from '../../../types/prioritization/catchballLoop';
import type { CatchballProposal } from '../../../types/prioritization/catchballProposal';
import { AuthorizationGate } from '../AuthorizationGate';
import type { ResourceContext } from '../../../lib/authorization/types';
import { CATCHBALL_LABELS } from '../../../lib/catchball/vocabulary';

interface EvaluatorResponseFrameProps {
  originalIntent: LoopIntent;
  response: {
    proposals: CatchballProposal[];
    synthesis?: string;
    rationale?: string;
  };
  isLoading?: boolean;
  /**
   * Accept. Null when the person may not accept (D-4, migration 987): the
   * Accept control is not drawn, and `acceptLine` says what it takes.
   */
  onAccept: (() => void) | null;
  /** The plain line shown in place of Accept when `onAccept` is null. */
  acceptLine?: string | null;
  onRevise: (guidance: string) => void;
  onReject: (reason: string) => void;
  /**
   * False when the cycle's decision right shuts this person out of deciding
   * (migration 1001: rejecting and returning exercise the right accepting
   * does); Revise and Reject are then not drawn, and `acceptLine` says why.
   */
  mayDecide?: boolean;
  /**
   * Resource context for authorization checks (catchball loop / cycle).
   * When omitted, no authorization gate is applied (backward compatible).
   */
  resource?: ResourceContext | null;
}

/**
 * Wraps children with AuthorizationGate when a resource is provided.
 * Falls through unchanged when resource is undefined (callers that don't pass it).
 */
function GatedAction({
  resource,
  action,
  children,
}: {
  resource: ResourceContext | null | undefined;
  action: string;
  children: React.ReactNode;
}) {
  if (resource === undefined) return <>{children}</>;
  return (
    <AuthorizationGate action={action} resource={resource}>
      {children}
    </AuthorizationGate>
  );
}

function IntentColumn({ intent }: { intent: LoopIntent }) {
  const hasConstraints = (intent.constraints ?? []).length > 0;
  const hasGuardrails = (intent.guardrails ?? []).length > 0;
  const hasAsks = (intent.asks ?? []).length > 0;

  return (
    <div className="border border-[var(--slate)] bg-[var(--slate-dim)] p-5 space-y-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-sec)]">
        What you asked for
      </p>

      {intent.strategicContext && (
        <p className="text-sm font-medium text-[var(--text-pri)]">{intent.strategicContext}</p>
      )}

      {hasAsks && (
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <Target className="h-4 w-4 text-[var(--text-ter)]" />
            <span className="text-xs font-semibold uppercase tracking-wide text-[var(--text-sec)]">
              Goals / asks
            </span>
          </div>
          <ul className="space-y-1">
            {intent.asks.map((ask, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-[var(--text-sec)]">
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[var(--slate)] shrink-0" />
                {ask}
              </li>
            ))}
          </ul>
        </div>
      )}

      {hasConstraints && (
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <AlertCircle className="h-4 w-4 text-[var(--coral)]" />
            <span className="text-xs font-semibold uppercase tracking-wide text-[var(--coral)]">
              Constraints
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {intent.constraints.map((c, i) => (
              <span
                key={i}
                className="inline-flex rounded-full bg-[var(--coral-dim)] px-2.5 py-0.5 text-xs font-medium text-[var(--coral)]"
              >
                {c}
              </span>
            ))}
          </div>
        </div>
      )}

      {hasGuardrails && (
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <Shield className="h-4 w-4 text-[var(--mustard)]" />
            <span className="text-xs font-semibold uppercase tracking-wide text-[var(--mustard)]">
              Guardrails
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {intent.guardrails!.map((g, i) => (
              <span
                key={i}
                className="inline-flex rounded-full bg-[var(--mustard-dim)] px-2.5 py-0.5 text-xs font-medium text-[var(--mustard)]"
              >
                {g}
              </span>
            ))}
          </div>
        </div>
      )}

      {intent.responseGuidance && (
        <div className="bg-[var(--bg-raised)] border border-[var(--slate)] px-3 py-2">
          <p className="text-xs font-medium text-[var(--text-ter)] mb-1">What a good response looks like</p>
          <p className="text-xs text-[var(--text-sec)]">{intent.responseGuidance}</p>
        </div>
      )}
    </div>
  );
}

function ResponseColumn({
  response,
}: {
  response: EvaluatorResponseFrameProps['response'];
}) {
  return (
    <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-5 space-y-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)]">
        What came back
      </p>

      {response.synthesis && (
        <div>
          <p className="text-xs font-medium text-[var(--text-ter)] mb-1">Synthesis</p>
          <p className="text-sm text-[var(--text-pri)]">{response.synthesis}</p>
        </div>
      )}

      {response.rationale && !response.synthesis && (
        <div>
          <p className="text-xs font-medium text-[var(--text-ter)] mb-1">Rationale</p>
          <p className="text-sm text-[var(--text-pri)]">{response.rationale}</p>
        </div>
      )}

      {response.proposals.length > 0 && (
        <div>
          <p className="text-xs font-medium text-[var(--text-ter)] mb-2">
            Proposals ({response.proposals.length})
          </p>
          <div className="space-y-2">
            {response.proposals.map((proposal) => (
              <div
                key={proposal.id}
                className="border border-[var(--rule)] bg-[var(--surface)] px-3 py-2"
              >
                <p className="text-sm font-medium text-[var(--text-pri)] line-clamp-2">
                  {proposal.rationale || 'No rationale provided'}
                </p>
                <div className="mt-1 flex items-center gap-2 text-xs text-[var(--text-ter)]">
                  <span>{proposal.proposedByName ?? proposal.proposedBy ?? 'Unknown'}</span>
                  <span>·</span>
                  <span>
                    {new Date(proposal.proposedAt).toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric',
                    })}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {response.proposals.length === 0 && !response.synthesis && !response.rationale && (
        <p className="text-sm text-[var(--text-ter)] italic">No response content yet.</p>
      )}
    </div>
  );
}

export function EvaluatorResponseFrame({
  originalIntent,
  response,
  isLoading = false,
  onAccept,
  acceptLine = null,
  onRevise,
  onReject,
  mayDecide = true,
  resource,
}: EvaluatorResponseFrameProps) {
  const [hasReviewed, setHasReviewed] = useState(false);
  const [showReviseForm, setShowReviseForm] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [reviseGuidance, setReviseGuidance] = useState('');
  const [rejectReason, setRejectReason] = useState('');

  return (
    <div className="space-y-4">
      {/* Two-column intent vs response */}
      <div className="grid gap-4 md:grid-cols-2">
        <IntentColumn intent={originalIntent} />
        <ResponseColumn response={response} />
      </div>

      {/* Review confirmation (gates Accept CTA) */}
      {onAccept && !hasReviewed && (
        <div className="flex items-center gap-3 bg-[var(--surface)] border border-[var(--rule)] px-4 py-3">
          <input
            id="reviewed-confirm"
            type="checkbox"
            className="rounded border-[var(--rule)] text-[var(--text-ter)] focus:ring-indigo-500"
            onChange={(e) => setHasReviewed(e.target.checked)}
          />
          <label htmlFor="reviewed-confirm" className="text-sm text-[var(--text-sec)] cursor-pointer">
            I've reviewed the response above
          </label>
        </div>
      )}

      {/* Decision CTAs */}
      {!showReviseForm && !showRejectForm && (
        <div className="flex flex-wrap items-start gap-3 pt-1">
          {/* Accept — only active once reviewed; not drawn for a person who may not accept */}
          {!onAccept ? (
            acceptLine ? <p className="self-center text-sm text-[var(--text-ter)]">{acceptLine}</p> : null
          ) : hasReviewed ? (
            <GatedAction resource={resource} action="catchball.accept">
              <button
                type="button"
                onClick={onAccept}
                disabled={isLoading}
                className="inline-flex items-center gap-2 bg-[var(--green)] px-5 py-2.5 text-sm font-medium text-white hover:bg-[var(--green)] disabled:opacity-60"
              >
                <CheckCircle2 className="h-4 w-4" />
                Accept
              </button>
            </GatedAction>
          ) : (
            <div className="inline-flex items-center gap-2 bg-[var(--hover-tint)] px-5 py-2.5 text-sm font-medium text-[var(--text-ter)] cursor-not-allowed select-none">
              <CheckCircle2 className="h-4 w-4" />
              Accept
              <span className="text-xs font-normal">(review first)</span>
            </div>
          )}

          {mayDecide && (<>
          {/* Revise */}
          <GatedAction resource={resource} action="catchball.return">
            <button
              type="button"
              onClick={() => {
                setShowReviseForm(true);
                setShowRejectForm(false);
              }}
              disabled={isLoading}
              className="inline-flex items-center gap-2 border border-[var(--slate)] bg-[var(--bg-raised)] px-5 py-2.5 text-sm font-medium text-[var(--text-ter)] hover:bg-[var(--slate-dim)] disabled:opacity-60"
            >
              <RotateCcw className="h-4 w-4" />
              {CATCHBALL_LABELS.returnForRevision}
            </button>
          </GatedAction>

          {/* Reject */}
          <GatedAction resource={resource} action="catchball.reject">
            <button
              type="button"
              onClick={() => {
                setShowRejectForm(true);
                setShowReviseForm(false);
              }}
              disabled={isLoading}
              className="inline-flex items-center gap-2 border border-[var(--coral)] px-5 py-2.5 text-sm font-medium text-[var(--coral)] hover:bg-[var(--coral-dim)] disabled:opacity-60"
            >
              <XCircle className="h-4 w-4" />
              Reject
            </button>
          </GatedAction>
          </>)}
        </div>
      )}

      {/* Inline revise form */}
      {showReviseForm && (
        <div className="border border-[var(--slate)] bg-[var(--slate-dim)] p-5 space-y-4">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-[var(--text-ter)]" />
            <h3 className="text-sm font-semibold text-[var(--text-sec)]">{CATCHBALL_LABELS.returnForRevision}</h3>
          </div>
          <p className="text-sm text-[var(--text-sec)]">
            Describe what needs to change. This guidance will travel back down with the ball.
          </p>
          <textarea
            value={reviseGuidance}
            onChange={(e) => setReviseGuidance(e.target.value)}
            rows={4}
            placeholder="What specifically needs to change? What would make this acceptable?"
            className="w-full border border-[var(--slate)] bg-[var(--bg-raised)] px-4 py-3 text-sm text-[var(--text-pri)] focus:border-[var(--slate)] focus:outline-none focus:ring-2 focus:ring-indigo-100"
          />
          <div className="flex gap-3">
            <GatedAction resource={resource} action="catchball.return">
              <button
                type="button"
                onClick={() => {
                  onRevise(reviseGuidance.trim());
                  setShowReviseForm(false);
                  setReviseGuidance('');
                }}
                disabled={isLoading || !reviseGuidance.trim()}
                className="inline-flex items-center gap-2 bg-[var(--slate)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--slate)] disabled:opacity-60"
              >
                <RotateCcw className="h-4 w-4" />
                Confirm return
              </button>
            </GatedAction>
            <button
              type="button"
              onClick={() => {
                setShowReviseForm(false);
                setReviseGuidance('');
              }}
              className="border border-[var(--rule)] px-4 py-2 text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--surface)]"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Inline reject form */}
      {showRejectForm && (
        <div className="border border-[var(--coral)] bg-[var(--coral-dim)] p-5 space-y-4">
          <div className="flex items-center gap-2">
            <XCircle className="h-4 w-4 text-[var(--coral)]" />
            <h3 className="text-sm font-semibold text-[var(--coral)]">Reject proposal</h3>
          </div>
          <div className="bg-[var(--coral-dim)] border border-[var(--coral)] px-3 py-2">
            <p className="text-xs font-medium text-[var(--coral)]">
              Warning: Rejecting this proposal closes the loop and restarts the work from the
              beginning. This cannot be undone.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Reason for rejection (required)
            </label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              placeholder="Why is this proposal being rejected? What would need to be true for a future proposal to be accepted?"
              className="w-full border border-[var(--coral)] bg-[var(--bg-raised)] px-4 py-3 text-sm text-[var(--text-pri)] focus:border-[var(--coral)] focus:outline-none focus:ring-2 focus:ring-red-100"
            />
          </div>
          <div className="flex gap-3">
            <GatedAction resource={resource} action="catchball.reject">
              <button
                type="button"
                onClick={() => {
                  onReject(rejectReason.trim());
                  setShowRejectForm(false);
                  setRejectReason('');
                }}
                disabled={isLoading || !rejectReason.trim()}
                className="inline-flex items-center gap-2 bg-[var(--coral)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--coral)] disabled:opacity-60"
              >
                <XCircle className="h-4 w-4" />
                Confirm reject
              </button>
            </GatedAction>
            <button
              type="button"
              onClick={() => {
                setShowRejectForm(false);
                setRejectReason('');
              }}
              className="border border-[var(--rule)] px-4 py-2 text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--surface)]"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default EvaluatorResponseFrame;
